const RESULT_VALUES = new Set(['1-0', '0-1', '1/2-1/2', '*']);

function normalizeText(value, fallback = '') {
    const text = String(value ?? '').trim();
    return text || fallback;
}

function escapeTagValue(value) {
    return normalizeText(value, '?')
        .replaceAll('\\', '\\\\')
        .replaceAll('"', '\\"');
}

function sanitizeFilenamePart(value) {
    return normalizeText(value, 'game')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48) || 'game';
}

function getPlayerName(player, fallback = '?') {
    if (typeof player === 'string') {
        return normalizeText(player, fallback);
    }
    if (!player || typeof player !== 'object') {
        return fallback;
    }
    return normalizeText(
        player.name || player.displayName || player.fullName || player.username || player.titleName,
        fallback
    );
}

function normalizeResult(value) {
    const result = normalizeText(value, '*');
    return RESULT_VALUES.has(result) ? result : '*';
}

function normalizeDate(value) {
    const date = normalizeText(value, '????.??.??');
    if (/^\d{4}\.\d{2}\.\d{2}$/.test(date)) {
        return date;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date.replaceAll('-', '.');
    }
    if (/^\d{4}-\d{2}$/.test(date)) {
        return `${date.replace('-', '.')}.??`;
    }
    if (/^\d{4}$/.test(date)) {
        return `${date}.??.??`;
    }
    return date || '????.??.??';
}

function getTagEntries(replayState = {}) {
    const meta = replayState.meta || {};
    const white = replayState.white || {};
    const black = replayState.black || {};
    const result = normalizeResult(meta.result || replayState.result);

    const entries = [
        ['Event', normalizeText(meta.event, '?')],
        ['Site', normalizeText(meta.site, '?')],
        ['Date', normalizeDate(meta.date)],
        ['Round', normalizeText(meta.round, '?')],
        ['White', getPlayerName(white, '?')],
        ['Black', getPlayerName(black, '?')],
        ['Result', result]
    ];

    const optionalTags = [
        ['WhiteElo', white.elo || white.rating || meta.whiteElo],
        ['BlackElo', black.elo || black.rating || meta.blackElo],
        ['ECO', meta.eco],
        ['Opening', meta.opening],
        ['TimeControl', meta.timeControl],
        ['Variant', meta.variant],
        ['Source', meta.source],
        ['SourceUrl', meta.sourceGameUrl || meta.sourceUrl]
    ];

    optionalTags.forEach(([tag, value]) => {
        const text = normalizeText(value);
        if (text) {
            entries.push([tag, text]);
        }
    });

    return entries;
}

function groupVariationsByParentPly(variations = []) {
    const grouped = new Map();
    (Array.isArray(variations) ? variations : []).forEach((variation) => {
        const parentPly = Number(variation?.parentPly);
        const moves = Array.isArray(variation?.moves) ? variation.moves : [];
        if (!Number.isInteger(parentPly) || parentPly < 0 || !moves.length) {
            return;
        }
        const bucket = grouped.get(parentPly) || [];
        bucket.push(variation);
        grouped.set(parentPly, bucket);
    });
    return grouped;
}

function parseFenMoveContext(fen) {
    const parts = String(fen || '').trim().split(/\s+/);
    const activeColor = parts[1] === 'b' ? 'b' : 'w';
    const parsedFullmove = parseInt(parts[5], 10);
    return {
        sideToMove: activeColor,
        fullmoveNumber: Number.isInteger(parsedFullmove) && parsedFullmove > 0
            ? parsedFullmove
            : 1
    };
}

function advanceMoveContext(context) {
    const sideToMove = context.sideToMove === 'b' ? 'b' : 'w';
    return {
        sideToMove: sideToMove === 'w' ? 'b' : 'w',
        fullmoveNumber: sideToMove === 'b'
            ? context.fullmoveNumber + 1
            : context.fullmoveNumber
    };
}

function contextAfterPlies(startContext, plies) {
    let context = { ...startContext };
    const count = Math.max(0, Number.isInteger(plies) ? plies : 0);
    for (let i = 0; i < count; i += 1) {
        context = advanceMoveContext(context);
    }
    return context;
}

function moveTokenForSan(san, context, isFirstMoveInLine = false) {
    if (context.sideToMove === 'w') {
        return `${context.fullmoveNumber}. ${san}`;
    }
    return isFirstMoveInLine
        ? `${context.fullmoveNumber}... ${san}`
        : san;
}

function formatMoveLine(moves = [], startContext, options = {}) {
    const tokens = [];
    let context = { ...startContext };
    const getVariationsAfterPly = typeof options.getVariationsAfterPly === 'function'
        ? options.getVariationsAfterPly
        : null;
    const startingPlyOffset = Number.isInteger(options.startingPlyOffset)
        ? options.startingPlyOffset
        : 0;

    (Array.isArray(moves) ? moves : []).forEach((move, index) => {
        const san = normalizeText(typeof move === 'string' ? move : move?.san);
        if (!san) {
            return;
        }

        tokens.push(moveTokenForSan(san, context, tokens.length === 0));
        context = advanceMoveContext(context);

        if (getVariationsAfterPly) {
            const parentPly = startingPlyOffset + index + 1;
            const ravs = getVariationsAfterPly(parentPly);
            ravs.forEach((rav) => {
                if (rav) {
                    tokens.push(rav);
                }
            });
        }
    });

    return tokens;
}

function formatVariation(variation, startContext) {
    const parentPly = Number(variation?.parentPly);
    const moves = Array.isArray(variation?.moves) ? variation.moves : [];
    const variationContext = contextAfterPlies(startContext, parentPly);
    const tokens = formatMoveLine(moves, variationContext);
    return tokens.length ? `(${tokens.join(' ')})` : '';
}

function wrapMoveText(tokens, maxLineLength = 86) {
    const lines = [];
    let line = '';

    tokens.forEach((token) => {
        if (!token) {
            return;
        }
        const next = line ? `${line} ${token}` : token;
        if (line && next.length > maxLineLength) {
            lines.push(line);
            line = token;
        } else {
            line = next;
        }
    });

    if (line) {
        lines.push(line);
    }
    return lines.join('\n');
}

function buildMoveTokens(mainlineMoves = [], variationsByParentPly = new Map(), result = '*', startContext = parseFenMoveContext('')) {
    const moves = Array.isArray(mainlineMoves) ? mainlineMoves : [];
    const tokens = [];

    (variationsByParentPly.get(0) || []).forEach((variation) => {
        const rav = formatVariation(variation, startContext);
        if (rav) {
            tokens.push(rav);
        }
    });

    tokens.push(...formatMoveLine(moves, startContext, {
        getVariationsAfterPly(parentPly) {
            return (variationsByParentPly.get(parentPly) || [])
                .map((variation) => formatVariation(variation, startContext))
                .filter(Boolean);
        }
    }));

    tokens.push(normalizeResult(result));
    return tokens;
}

export function buildPgnWithVariations({ replayState = {}, variations = [] } = {}) {
    const tagEntries = getTagEntries(replayState);
    const result = normalizeResult(replayState.meta?.result || replayState.result);
    const variationMap = groupVariationsByParentPly(variations);
    const moveTokens = buildMoveTokens(
        replayState.moves || [],
        variationMap,
        result,
        parseFenMoveContext(replayState.startFen)
    );

    const tags = tagEntries
        .map(([tag, value]) => `[${tag} "${escapeTagValue(value)}"]`)
        .join('\n');
    return `${tags}\n\n${wrapMoveText(moveTokens)}\n`;
}

export function buildAnalysisFilename(replayState = {}) {
    const meta = replayState.meta || {};
    const explicitFilename = normalizeText(meta.exportFilename);
    if (explicitFilename) {
        return explicitFilename.endsWith('.pgn') ? explicitFilename : `${explicitFilename}.pgn`;
    }
    const white = sanitizeFilenamePart(getPlayerName(replayState.white, 'white'));
    const black = sanitizeFilenamePart(getPlayerName(replayState.black, 'black'));
    const date = normalizeDate(meta.date);
    const yearMatch = /^(\d{4})/.exec(date);
    const year = yearMatch ? yearMatch[1] : 'game';
    return `${white}-${black}-${year}-felixchess-analysis.pgn`;
}

export function downloadTextFile(filename, text, mimeType = 'application/x-chess-pgn;charset=utf-8') {
    const blob = new Blob([text], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'felixchess-analysis.pgn';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, 0);
}
